import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid2";
import Stack from "@mui/material/Stack";
import useTheme from "@mui/material/styles/useTheme";
import Typography from "@mui/material/Typography";
import { CardElement } from "@recurly/react-recurly";
import { useFormik } from "formik";
import { FC, useState } from "react";
import * as Yup from "yup";
import { StepIcon } from "~ui-components/components/atoms/step-icon";
import { CountriesSelectField } from "./countries-select-field";
import { InputField } from "./input-field";

export const AccountBillingSchema = Yup.object({
	companyName: Yup.string(),
	vatNumber: Yup.string().when("country", {
		is: "GB",
		then: (schema) => schema.required("Required"),
		otherwise: (schema) => schema.notRequired(),
	}),
	firstName: Yup.string().required("Required"),
	lastName: Yup.string().required("Required"),
	streetLine1: Yup.string().required("Required"),
	streetLine2: Yup.string(),
	city: Yup.string().required("Required"),
	province: Yup.string().required("Required"),
	postalCode: Yup.string().required("Required"),
	country: Yup.string().required("Required"),
});

export type AccountBillingFormValues = Yup.InferType<
	typeof AccountBillingSchema
>;

interface AccountBillingFormProps {
	form: ReturnType<typeof useFormik<AccountBillingFormValues>>;
}

export const AccountBillingForm: FC<AccountBillingFormProps> = ({ form }) => {
	const theme = useTheme();

	const [isCardInputError, setIsCardInputError] = useState(false);

	return (
		<Card>
			<CardContent>
				<Stack spacing={6}>
					<Stack spacing={3}>
						<Stack
							direction="row"
							spacing={2}
							sx={{ alignItems: "center" }}>
							<StepIcon number={2} />
							<Typography variant="h6">Billing card</Typography>
						</Stack>
						<Grid
							container
							spacing={3}>
							<Grid
								size={{
									xs: 12,
									xl: 6,
								}}>
								<Box
									sx={{
										".recurly-element": {
											"--Input-borderRadius": "8px",
											"--Input-borderWidth": "1px",
											"--Input-borderColor":
												"var(--mui-palette-OutlinedInput-border)",
											border:
												"var(--Input-borderWidth) solid var(--Input-borderColor)",
											borderColor: isCardInputError
												? theme.palette.error.main
												: "var(--Input-borderColor)",
											borderRadius: "var(--Input-borderRadius)",
											height: "40px",
										},
									}}>
									<CardElement
										style={{
											fontWeight: theme.typography.body1.fontWeight!.toString(),
											fontSize: theme.typography.body1.fontSize!.toString(),
											fontFamily: theme.typography.body1.fontFamily,
											lineHeight: theme.typography.body1.lineHeight!.toString(),
											fontColor: theme.palette.text.primary,
											invalid: {
												fontColor: theme.palette.text.primary,
											},
										}}
										onBlur={() => setIsCardInputError(!form.status.isCardValid)}
										onFocus={() => setIsCardInputError(false)}
										onChange={(e) => {
											form.setStatus({
												...form.status,
												isCardValid: e.valid,
											});
										}}
									/>
									{isCardInputError && (
										<FormControl>
											<FormHelperText error>
												Invalid card details
											</FormHelperText>
										</FormControl>
									)}
								</Box>
							</Grid>
						</Grid>
					</Stack>
					<Stack spacing={3}>
						<Stack
							direction="row"
							spacing={2}
							sx={{ alignItems: "center" }}>
							<StepIcon number={1} />
							<Typography variant="h6">Billing address</Typography>
						</Stack>
						<Grid
							container
							spacing={3}>
							<InputField
								form={form}
								name="firstName"
								label="First Name"
							/>
							<InputField
								form={form}
								name="lastName"
								label="Last Name"
							/>
							<InputField
								form={form}
								name="streetLine1"
								label="Street line 1"
							/>
							<InputField
								form={form}
								name="streetLine2"
								label="Street line 2 (optional)"
							/>
							<InputField
								form={form}
								name="city"
								label="City"
							/>
							<InputField
								form={form}
								name="province"
								label="State / Province"
							/>
							<InputField
								form={form}
								name="postalCode"
								label="Zip / Postal code"
							/>
							<CountriesSelectField
								form={form}
								name="country"
								label="Country"
							/>
							<InputField
								form={form}
								name="companyName"
								label="Company Name (optional)"
							/>
							{form.values.country === "GB" && (
								<InputField
									form={form}
									name="vatNumber"
									label="VAT number"
								/>
							)}
						</Grid>
					</Stack>
				</Stack>
			</CardContent>
		</Card>
	);
};
